import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

/**
 * Compliance Mode Context
 *
 * Manages dual-mode state for PRISM Risk Aperture platform:
 * - FEDERAL: DoD/Federal terminology (RMF, NIST 800-53, CMMC, FedRAMP)
 * - PRIVATE: Private sector terminology (GRC, ISO 27001, SOC 2, PCI DSS)
 *
 * This context enables mode-aware terminology, workflows, and UI elements
 * throughout the application.
 */

export type ComplianceMode = 'FEDERAL' | 'PRIVATE';

export interface ComplianceModeContextType {
  mode: ComplianceMode;
  setMode: (mode: ComplianceMode) => void;
  toggleMode: () => void;
  isFederal: boolean;
  isPrivate: boolean;
}

const ComplianceModeContext = createContext<ComplianceModeContextType | undefined>(undefined);

interface ComplianceModeProviderProps {
  children: ReactNode;
  defaultMode?: ComplianceMode;
}

/**
 * ComplianceModeProvider
 *
 * Wraps the application to provide compliance mode state management.
 * Persists mode preference to localStorage for session continuity.
 */
export const ComplianceModeProvider: React.FC<ComplianceModeProviderProps> = ({
  children,
  defaultMode = 'FEDERAL'
}) => {
  // Initialize from localStorage or use default
  const [mode, setModeState] = useState<ComplianceMode>(() => {
    const stored = localStorage.getItem('complianceMode');
    return (stored === 'FEDERAL' || stored === 'PRIVATE') ? stored : defaultMode;
  });

  const setMode = useCallback((newMode: ComplianceMode) => {
    setModeState(newMode);
    localStorage.setItem('complianceMode', newMode);
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === 'FEDERAL' ? 'PRIVATE' : 'FEDERAL');
  }, [mode, setMode]);

  const value: ComplianceModeContextType = {
    mode,
    setMode,
    toggleMode,
    isFederal: mode === 'FEDERAL',
    isPrivate: mode === 'PRIVATE'
  };

  return (
    <ComplianceModeContext.Provider value={value}>
      {children}
    </ComplianceModeContext.Provider>
  );
};

/**
 * useComplianceMode Hook
 *
 * Access compliance mode state and helpers from any component.
 *
 * @example
 * const { mode, isFederal, toggleMode } = useComplianceMode();
 * const term = isFederal ? 'ATO' : 'Certification';
 */
export const useComplianceMode = (): ComplianceModeContextType => {
  const context = useContext(ComplianceModeContext);
  if (!context) {
    throw new Error('useComplianceMode must be used within a ComplianceModeProvider');
  }
  return context;
};
